// ==================== UPSELL MODAL CONTROLLER ====================

// Cache modal elements to avoid repeated DOM queries
let modalCache = null;
let modalElementsCache = null;

function getModalCache() {
  if (!modalCache) {
    modalCache = document.querySelector("[data-upsell-modal]");
    if (modalCache) {
      modalElementsCache = {
        modal: modalCache,
        title: modalCache.querySelector("[data-upsell-title]"),
        price: modalCache.querySelector("[data-upsell-price]"),
        priceBtn: modalCache.querySelector("[data-upsell-price-btn]"),
        image: modalCache.querySelector("[data-upsell-image]"),
        imageThumbnails: modalCache.querySelector("[data-upsell-image-thumbnails]"),
        atc: modalCache.querySelector("[data-upsell-atc]"),
        rating: modalCache.querySelector("[data-upsell-rating]"),
        ratingPlaceholder: modalCache.querySelector("[data-upsell-rating-placeholder]"),
        styleContainer: modalCache.querySelector("[data-upsell-style]"),
        styleContent: modalCache.querySelector("[data-upsell-style-content]"),
        sizeChart: modalCache.querySelector("[data-upsell-size-chart]"),
        description: modalCache.querySelector("[data-upsell-description]"),
        descriptionContent: modalCache.querySelector("[data-upsell-description-content]"),
        variantsContainer: modalCache.querySelector("[data-upsell-variants]"),
        swatches: modalCache.querySelector("[data-upsell-swatches]"),
        priorityBox: modalCache.querySelector("[data-upsell-priority]"),
        priorityCheckbox: modalCache.querySelector("[data-priority-checkbox]"),
        priorityPrice: modalCache.querySelector("[data-priority-price]"),
        priorityImage: modalCache.querySelector("[data-priority-image]")
      };
    }
  }
  return modalElementsCache;
}

// Cache for parsed product data and fetched Judge.me data
const productDataCache = new Map();
const judgeMeCache = new Map();

// Function to populate rating (moved outside to be reusable)
function populateUpsellRating(modal, jdgmRating, jdgmCount, productId, fallbackRating, fallbackRatingMax, fallbackRatingCount) {
  const ratingContainer = modal.querySelector("[data-upsell-rating]");
  const placeholder = modal.querySelector("[data-upsell-rating-placeholder]");
  
  // Hide placeholder when reviews are loaded
  if (placeholder) {
    placeholder.style.display = 'none';
  }
  
  // Check if Judge.me has ratings
  if (jdgmRating > 0 && jdgmCount > 0) {
    // Render Judge.me stars (same structure as jdgm-preview-badge)
    const fullStars = Math.floor(jdgmRating);
    const hasHalfStar = (jdgmRating % 1) >= 0.5;
    
    ratingContainer.innerHTML = `
      <div class="jdgm-widget jdgm-preview-badge jdgm-preview-badge--with-link" data-id="${productId}">
        <div class="jdgm-prev-badge" data-average-rating="${jdgmRating}" data-number-of-reviews="${jdgmCount}">
          <span class="jdgm-prev-badge__stars" data-score="${jdgmRating}" tabindex="0" aria-label="${jdgmRating} stars" role="button">
            ${Array(5).fill(0).map((_, i) => {
              if (i < fullStars) {
                return '<span class="jdgm-star jdgm--on"></span>';
              } else if (i === fullStars && hasHalfStar) {
                return '<span class="jdgm-star jdgm--half"></span>';
              } else {
                return '<span class="jdgm-star"></span>';
              }
            }).join('')}
          </span>
          <span class="jdgm-prev-badge__text">${jdgmCount} reviews</span>
        </div>
      </div>
    `;
    ratingContainer.style.display = 'block';
  } else if (fallbackRating && fallbackRating > 0) {
    // Fall back to standard reviews format
    const rating = parseFloat(fallbackRating) || 0;
    const ratingMax = parseFloat(fallbackRatingMax) || 5;
    const ratingCount = parseInt(fallbackRatingCount) || 0;
    
    // Calculate rating decimal for half stars
    let ratingDecimal = 0;
    const decimal = rating % 1;
    if (decimal >= 0.3 && decimal <= 0.7) {
      ratingDecimal = 0.5;
    } else if (decimal > 0.7) {
      ratingDecimal = 1;
    }
    
    const ratingFloor = Math.floor(rating);
    
    ratingContainer.innerHTML = `
      <div class="rating" role="img" aria-label="${rating} stars, ${ratingCount} reviews">
        <span class="rating-star" style="--rating: ${ratingFloor}; --rating-max: ${ratingMax}; --rating-decimal: ${ratingDecimal};"></span>
      </div>
      <p class="rating-count caption">
        <span aria-hidden="true">(${ratingCount})</span>
      </p>
    `;
    ratingContainer.style.display = 'block';
  } else {
    // No reviews available - hide both placeholder and container
    ratingContainer.style.display = 'none';
    if (placeholder) {
      placeholder.style.display = 'none';
    }
  }
}

// Optimized function to fetch Judge.me data with caching
async function fetchJudgeMeData(productHandle, productId) {
  // Validate inputs
  if (!productHandle || !productId) {
    return { jdgmRating: 0, jdgmCount: 0 };
  }
  
  const cacheKey = `${productHandle}-${productId}`;
  
  // Check cache first
  if (judgeMeCache.has(cacheKey)) {
    return judgeMeCache.get(cacheKey);
  }
  
  try {
    const productUrl = `/products/${productHandle}`;
    const response = await fetch(productUrl, {
      method: 'GET',
      headers: {
        'Accept': 'text/html'
      }
    });
    
    // Check if response is ok
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Try to find Judge.me widget data
    const jdgmWidget = doc.querySelector('.jdgm-prev-badge');
    let jdgmRating = 0;
    let jdgmCount = 0;
    
    if (jdgmWidget) {
      jdgmRating = parseFloat(jdgmWidget.getAttribute('data-average-rating')) || 0;
      jdgmCount = parseInt(jdgmWidget.getAttribute('data-number-of-reviews')) || 0;
    }
    
    const result = { jdgmRating, jdgmCount };
    judgeMeCache.set(cacheKey, result);
    return result;
  } catch (error) {
    // Silently fail - don't log to console in production
    // Only log in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log('Could not fetch Judge.me data:', error.message);
    }
    // Cache the failure to avoid repeated attempts
    const result = { jdgmRating: 0, jdgmCount: 0 };
    judgeMeCache.set(cacheKey, result);
    return result;
  }
}

// Helper function to render image gallery thumbnails
function renderImageGallery(images, container, mainImageElement) {
  if (!container || !images || images.length <= 1) {
    container.innerHTML = '';
    container.style.display = 'none';
    return;
  }

  const needsSlider = images.length > 5;
  
  // Clear container and create wrapper if slider is needed
  container.innerHTML = '';
  
  let thumbnailsContainer = container;
  let prevButton = null;
  let nextButton = null;
  
  if (needsSlider) {
    // Create wrapper for slider
    const wrapper = document.createElement('div');
    wrapper.className = 'upsell-thumbnail-slider-wrapper';
    
    // Create arrow buttons
    prevButton = document.createElement('button');
    prevButton.type = 'button';
    prevButton.className = 'upsell-thumbnail-slider-arrows upsell-thumbnail-slider-arrows--prev';
    prevButton.setAttribute('aria-label', 'Previous thumbnails');
    prevButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    
    nextButton = document.createElement('button');
    nextButton.type = 'button';
    nextButton.className = 'upsell-thumbnail-slider-arrows upsell-thumbnail-slider-arrows--next';
    nextButton.setAttribute('aria-label', 'Next thumbnails');
    nextButton.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    
    // Create inner container for thumbnails
    thumbnailsContainer = document.createElement('div');
    thumbnailsContainer.className = 'upsell-image-thumbnails is-slider';
    
    wrapper.appendChild(prevButton);
    wrapper.appendChild(thumbnailsContainer);
    wrapper.appendChild(nextButton);
    
    container.appendChild(wrapper);
    container.style.display = 'flex';
    
    // Add slider functionality
    const updateArrows = () => {
      const scrollLeft = thumbnailsContainer.scrollLeft;
      const scrollWidth = thumbnailsContainer.scrollWidth;
      const clientWidth = thumbnailsContainer.clientWidth;
      
      prevButton.disabled = scrollLeft === 0;
      nextButton.disabled = scrollLeft >= scrollWidth - clientWidth - 1;
    };
    
    prevButton.addEventListener('click', () => {
      const thumbnailWidth = thumbnailsContainer.querySelector('.upsell-thumbnail')?.offsetWidth || 60;
      const gap = 8;
      const scrollAmount = thumbnailWidth + gap;
      thumbnailsContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });
    
    nextButton.addEventListener('click', () => {
      const thumbnailWidth = thumbnailsContainer.querySelector('.upsell-thumbnail')?.offsetWidth || 60;
      const gap = 8;
      const scrollAmount = thumbnailWidth + gap;
      thumbnailsContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
    
    thumbnailsContainer.addEventListener('scroll', updateArrows);
    // Initial arrow state
    setTimeout(updateArrows, 100);
  } else {
    container.className = 'upsell-image-thumbnails';
    thumbnailsContainer = container;
  }
  
  // Ensure container is visible (override inline display: none)
  container.style.display = 'flex';
  
  images.forEach((imgData, index) => {
    // Handle both string URLs and object format
    let imgUrl = typeof imgData === 'string' ? imgData : (imgData.src || imgData);
    // Ensure URL is complete (add https: if needed for protocol-relative URLs)
    if (imgUrl.startsWith('//')) {
      imgUrl = 'https:' + imgUrl;
    }
    
    // Use thumbnail URL if provided, otherwise generate optimized thumbnail URL
    let thumbnailUrl;
    if (typeof imgData === 'object' && imgData.thumbnail) {
      thumbnailUrl = imgData.thumbnail.startsWith('//') ? 'https:' + imgData.thumbnail : imgData.thumbnail;
    } else {
      // Generate optimized thumbnail URL (120px for 60px display with 2x retina support)
      thumbnailUrl = imgUrl;
      if (thumbnailUrl.includes('width=')) {
        thumbnailUrl = thumbnailUrl.replace(/width=\d+/, 'width=120');
      } else if (thumbnailUrl.includes('&width=')) {
        thumbnailUrl = thumbnailUrl.replace(/&width=\d+/, '&width=120');
      } else {
        const separator = thumbnailUrl.includes('?') ? '&' : '?';
        thumbnailUrl = thumbnailUrl + separator + 'width=120';
      }
    }
    
    // Full-size URL for main image (use src if available, otherwise use imgUrl)
    let fullSizeUrl = imgUrl;
    if (typeof imgData === 'object' && imgData.src) {
      fullSizeUrl = imgData.src.startsWith('//') ? 'https:' + imgData.src : imgData.src;
    }
    
    const imgAlt = typeof imgData === 'object' && imgData.alt ? imgData.alt : '';
    
    const thumbnail = document.createElement('button');
    thumbnail.type = 'button';
    thumbnail.className = 'upsell-thumbnail' + (index === 0 ? ' active' : '');
    thumbnail.dataset.imageUrl = fullSizeUrl; // Store full-size URL for main image
    thumbnail.setAttribute('aria-label', `View image ${index + 1}`);
    // Use optimized thumbnail size with lazy loading and async decoding
    thumbnail.innerHTML = `<img src="${thumbnailUrl}" alt="${imgAlt}" loading="lazy" width="60" height="60" decoding="async">`;
    
    thumbnail.addEventListener('click', () => {
      // Update main image with full-size URL
      mainImageElement.classList.add("fade-out");
      setTimeout(() => {
        mainImageElement.src = fullSizeUrl;
        mainImageElement.alt = imgAlt;
        mainImageElement.classList.remove("fade-out");
        mainImageElement.classList.add("fade-in");
        setTimeout(() => mainImageElement.classList.remove("fade-in"), 300);
      }, 150);
      // Update active thumbnail
      thumbnailsContainer.querySelectorAll('.upsell-thumbnail').forEach(t => t.classList.remove('active'));
      thumbnail.classList.add('active');
      
      // Scroll active thumbnail into view if slider is active
      if (needsSlider && thumbnailsContainer) {
        const containerRect = thumbnailsContainer.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const scrollLeft = thumbnailsContainer.scrollLeft;
        const thumbnailOffset = thumbnail.offsetLeft;
        const thumbnailWidth = thumbnail.offsetWidth;
        const gap = 8;
        const containerWidth = thumbnailsContainer.clientWidth;
        
        // Calculate desired scroll position to center the thumbnail
        const desiredScrollLeft = thumbnailOffset - (containerWidth / 2) + (thumbnailWidth / 2);
        
        // Ensure we don't scroll beyond boundaries
        const maxScroll = thumbnailsContainer.scrollWidth - containerWidth;
        const clampedScroll = Math.max(0, Math.min(desiredScrollLeft, maxScroll));
        
        thumbnailsContainer.scrollTo({
          left: clampedScroll,
          behavior: 'smooth'
        });
        
        // Update arrow states after scroll
        setTimeout(() => {
          if (prevButton && nextButton) {
            const currentScroll = thumbnailsContainer.scrollLeft;
            prevButton.disabled = currentScroll === 0;
            nextButton.disabled = currentScroll >= maxScroll - 1;
          }
        }, 100);
      }
    });
    
    thumbnailsContainer.appendChild(thumbnail);
  });
  
  // Ensure thumbnails container is visible
  if (thumbnailsContainer && thumbnailsContainer !== container) {
    thumbnailsContainer.style.display = 'flex';
  }
  
  // Ensure wrapper is visible if it exists
  if (needsSlider && container.querySelector('.upsell-thumbnail-slider-wrapper')) {
    container.querySelector('.upsell-thumbnail-slider-wrapper').style.display = 'flex';
  }
}

// Set ATC button disabled state and text based on variant / product availability
function setUpsellAtcAvailability(atcButton, available) {
  if (!atcButton) return;
  const textEl = atcButton.querySelector(".upsell-atc-text");
  if (available) {
    atcButton.disabled = false;
    atcButton.removeAttribute("aria-disabled");
    if (textEl) textEl.textContent = "ADD TO CART";
  } else {
    atcButton.disabled = true;
    atcButton.setAttribute("aria-disabled", "true");
    if (textEl) textEl.textContent = "Sold out";
  }
}

// Build modal product object from a family item (swatch = other product in category)
function productFromFamilyItem(item) {
  return {
    id: item.variant,
    title: item.title,
    price: item.price,
    image: item.image,
    images: item.images || [],
    variants: item.variants || [],
    option_names: item.option_names || [],
    handle: item.handle,
    description: item.description || '',
    short_description: item.short_description || '',
    product_specs: item.product_specs || '',
    style: item.style || '',
    product_id: item.product_id,
    jdgm_rating: item.jdgm_rating,
    jdgm_count: item.jdgm_count,
    show_priority_upsell: false,
    priority_product: null,
    available: item.available !== false
  };
}

// Open upsell modal handler
document.addEventListener("click", async function (e) {
  const opener = e.target.closest(".upsell-open");
  if (!opener) return;

  e.preventDefault();
  e.stopPropagation();

  const scrollY = window.scrollY || window.pageYOffset;

  const elements = getModalCache();
  if (!elements) return;

  const modal = elements.modal;
  modal.classList.remove("is-hidden");
  
  document.body.classList.add("upsell-modal-open");

  const cacheKey = opener.dataset.upsell;
  let product, family;
  
  if (productDataCache.has(cacheKey)) {
    const cached = productDataCache.get(cacheKey);
    product = cached.product;
    family = cached.family;
  } else {
    try {
      product = JSON.parse(opener.dataset.upsell);
      family = JSON.parse(opener.dataset.upsellFamily || '[]');
    } catch (err) {
      console.warn('Upsell modal: Invalid product data (possible unescaped chars in description/specs).', err);
      modal.classList.add('is-hidden');
      document.body.classList.remove('upsell-modal-open');
      return;
    }
    productDataCache.set(cacheKey, { product, family });
  }

  const card = opener.closest(".product-card-wrapper");
  const selectedSwatch = card ? card.querySelector(".card__color-swatch.selected") : null;
  const selectedVariantId = selectedSwatch ? selectedSwatch.dataset.variantId : null;
  if (selectedVariantId && String(selectedVariantId) !== String(product.id) && Array.isArray(family) && family.length > 0) {
    const found = family.find(function (item) { return String(item.variant) === String(selectedVariantId); });
    if (found) {
      product = productFromFamilyItem(found);
    }
  }

  function restoreScroll() {
    window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
  }
  restoreScroll();
  requestAnimationFrame(restoreScroll);
  setTimeout(restoreScroll, 0);
  setTimeout(restoreScroll, 50);
  setTimeout(restoreScroll, 150);
  setTimeout(restoreScroll, 300);

  // Show placeholder reviews initially
  if (elements.ratingPlaceholder) {
    elements.ratingPlaceholder.style.display = 'block';
  }

  // Fill modal using cached elements
  elements.title.textContent = product.title;
  
  // Fix price display - strip HTML if present
  const priceText = typeof product.price === 'string' 
    ? product.price.replace(/<[^>]*>/g, '').trim() 
    : String(product.price || '');
  elements.price.textContent = priceText;
  elements.image.src = product.image;
  elements.atc.dataset.variantId = product.id;
  setUpsellAtcAvailability(elements.atc, product.available !== false);
  if (elements.priceBtn) {
    elements.priceBtn.textContent = priceText;
  }

  // Populate image gallery if product has multiple images
  if (product.images && Array.isArray(product.images) && product.images.length > 1 && elements.imageThumbnails) {
    renderImageGallery(product.images, elements.imageThumbnails, elements.image);
  } else if (elements.imageThumbnails) {
    elements.imageThumbnails.innerHTML = '';
    elements.imageThumbnails.style.display = 'none';
  }

  // Try to get Judge.me data from data attributes first
  let jdgmRating = parseFloat(product.jdgm_rating) || 0;
  let jdgmCount = parseInt(product.jdgm_count) || 0;
  
  // Check if there's already a Judge.me widget on the page for this product
  const existingWidget = document.querySelector(`.jdgm-preview-badge[data-id="${product.product_id || product.id}"] .jdgm-prev-badge`);
  if (existingWidget) {
    jdgmRating = parseFloat(existingWidget.getAttribute('data-average-rating')) || 0;
    jdgmCount = parseInt(existingWidget.getAttribute('data-number-of-reviews')) || 0;
  }
  
  // If still no data, try to fetch from product page (with caching)
  // Only fetch if we have a valid handle
  if ((!jdgmRating || !jdgmCount) && product.handle) {
    fetchJudgeMeData(product.handle, product.product_id || product.id).then(({ jdgmRating: fetchedRating, jdgmCount: fetchedCount }) => {
      populateUpsellRating(modal, fetchedRating, fetchedCount, product.product_id || product.id, product.rating, product.rating_max, product.rating_count);
    }).catch(() => {
      // Silently handle fetch errors - use fallback data if available
      if (product.rating && product.rating > 0) {
        populateUpsellRating(modal, 0, 0, product.product_id || product.id, product.rating, product.rating_max, product.rating_count);
      }
    });
  } else {
    // Use data from attributes or existing widget
    populateUpsellRating(modal, jdgmRating, jdgmCount, product.product_id || product.id, product.rating, product.rating_max, product.rating_count);
  }

  // Populate style
  if (product.style && product.style.trim() !== '') {
    elements.styleContent.textContent = product.style;
    elements.styleContainer.style.display = 'block';
  } else {
    elements.styleContainer.style.display = 'none';
  }

  // Populate size chart
  // Handle product_specs - it might be null, undefined, a string, or an object (rich text metafield)
  let productSpecs = '';
  if (product.product_specs) {
    if (typeof product.product_specs === 'string') {
      productSpecs = product.product_specs.trim();
    } else if (typeof product.product_specs === 'object') {
      // Handle rich text metafield object - try to extract text content
      // Rich text metafields have a 'value' property or might be HTML
      if (product.product_specs.value) {
        // Extract text from HTML if it's HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.product_specs.value;
        productSpecs = tempDiv.textContent || tempDiv.innerText || '';
      } else if (product.product_specs.html) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = product.product_specs.html;
        productSpecs = tempDiv.textContent || tempDiv.innerText || '';
      } else {
        // Try to stringify and extract text
        const jsonStr = JSON.stringify(product.product_specs);
        const tempDiv = document.createElement('div');
        tempDiv.textContent = jsonStr;
        productSpecs = tempDiv.textContent || '';
      }
      productSpecs = productSpecs.trim();
    } else {
      // If it's not a string or object, try to convert it
      productSpecs = String(product.product_specs).trim();
    }
  }
  
  // Show size chart only when product_specs metafield is present (same rule as pdp-size-chart snippet)
  if (product.show_size_chart && product.size_chart_url) {
    renderSizeChart(elements.sizeChart, productSpecs, product.size_chart_url);
    elements.sizeChart.style.display = 'block';
  } else {
    elements.sizeChart.style.display = 'none';
  }

  // Populate description - prefer short_description metafield, fallback to description
  const descriptionText = product.short_description || product.description || '';
  if (descriptionText && descriptionText.trim() !== '') {
    elements.descriptionContent.innerHTML = descriptionText;
    elements.description.style.display = 'block';
  } else {
    elements.description.style.display = 'none';
  }

  // Handle priority upsell product
  if (elements.priorityBox && product.show_priority_upsell && product.priority_product) {
    const priorityProduct = product.priority_product;
    if (priorityProduct && priorityProduct.variant_id && priorityProduct.price && priorityProduct.image) {
      elements.priorityCheckbox.dataset.priorityVariantId = priorityProduct.variant_id;
      elements.priorityPrice.textContent = `$${priorityProduct.price}`;
      elements.priorityImage.src = priorityProduct.image;
      elements.priorityImage.alt = priorityProduct.title || 'Skip the Line Priority';
      elements.priorityBox.style.display = 'block';
    } else {
      elements.priorityBox.style.display = 'none';
    }
  } else if (elements.priorityBox) {
    elements.priorityBox.style.display = 'none';
  }

  renderVariantOptions(product, elements.variantsContainer, elements.price, elements.atc);
  elements.swatches.innerHTML = ""; // clear skeletons

  // 1️⃣ Group products
  const groups = groupFamilyProducts(family);

  // Check if there are any swatches to show
  const hasSwatches = Object.values(groups).some(group => group.length > 0);

  if (hasSwatches) {
    // 2️⃣ Render grouped rows
    renderGroupedSwatches(groups, elements.swatches, modal);
    elements.swatches.style.display = 'flex';
    elements.swatches.style.visibility = 'visible';
    // Set active on the swatch that matches the current product (e.g. the one selected on the card)
    const currentVariantId = String(product.id);
    modal.querySelectorAll(".upsell-swatch, .upsell-swatch-image").forEach(s => s.classList.remove("active"));
    const toActivate = modal.querySelectorAll(`.upsell-swatch[data-variant-id="${currentVariantId}"], .upsell-swatch-image[data-variant-id="${currentVariantId}"]`);
    toActivate.forEach(s => s.classList.add("active"));
    if (toActivate.length === 0) {
      elements.swatches.querySelector(".upsell-swatch")?.classList.add("active");
    }
  } else {
    elements.swatches.style.display = 'none';
  }

  // Ensure form wrapper (swatches + variant options) is always visible so swatches show even when product is sold out
  const formWrapper = modal.querySelector(".upsell_form_wrapper");
  if (formWrapper) {
    formWrapper.style.display = 'block';
    formWrapper.style.visibility = 'visible';
  }
});

function renderVariantOptions(product, container, priceEl, atcEl) {
  if (!container) return;
  const variants = product.variants;
  if (!variants || variants.length < 2) {
    container.innerHTML = '';
    container.style.display = 'none';
    return;
  }
  const optionNames = product.option_names || [];
  let optionIndex = -1;
  for (let i = 0; i <= 2; i++) {
    const key = 'option' + (i + 1);
    const values = [...new Set(variants.map(v => v[key]).filter(Boolean))];
    if (values.length > 1) {
      optionIndex = i;
      break;
    }
  }
  const label = optionIndex >= 0 && optionNames[optionIndex] ? optionNames[optionIndex] : 'Variant';
  const key = optionIndex >= 0 ? 'option' + (optionIndex + 1) : null;
  container.innerHTML = '';
  const heading = document.createElement('div');
  heading.className = 'upsell-variant-heading';
  heading.textContent = label + ':';
  container.appendChild(heading);
  const row = document.createElement('div');
  row.className = 'upsell-variant-options';
  const currentId = String(product.id || product.variant || variants[0].id);
  variants.forEach((v) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'upsell-variant-option';
    btn.textContent = key ? (v[key] || v.title) : v.title;
    btn.dataset.variantId = v.id;
    btn.dataset.price = typeof v.price === 'string' ? v.price.replace(/<[^>]*>/g, '').trim() : String(v.price || '');
    if (String(v.id) === currentId) btn.classList.add('active');
    row.appendChild(btn);
  });
  container.appendChild(row);
  container.style.display = 'block';
}

function renderSizeChart(container, productSpecs, sizeChartUrl) {
  if (!sizeChartUrl) return;

  // productSpecs may contain HTML from metafields, insert it as-is
  // The parser will use innerText which strips HTML tags anyway
  const specsContent = productSpecs || '';

  container.innerHTML = `
    <button
      type="button"
      class="product_size_button"
      aria-expanded="false"
      data-sizechart-open
    >
      <svg width="25px" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M0.5 4C0.223858 4 0 4.22386 0 4.5V10.5C0 10.7761 0.223858 11 0.5 11H14.5C14.7761 11 15 10.7761 15 10.5V4.5C15 4.22386 14.7761 4 14.5 4H0.5ZM1 10V5H2.075V7.5C2.075 7.73472 2.26528 7.925 2.5 7.925C2.73472 7.925 2.925 7.73472 2.925 7.5V5H4.075V6.5C4.075 6.73472 4.26528 6.925 4.5 6.925C4.73472 6.925 4.925 6.73472 4.925 6.5V5H6.075V6.5C6.075 6.73472 6.26528 6.925 6.5 6.925C6.73472 6.925 6.925 6.73472 6.925 6.5V5H8.075V7.5C8.075 7.73472 8.26528 7.925 8.5 7.925C8.73472 7.925 8.925 7.73472 8.925 7.5V5H10.075V6.5C10.075 6.73472 10.2653 6.925 10.5 6.925C10.7347 6.925 10.925 6.73472 10.925 6.5V5H12.075V6.5C12.075 6.73472 12.2653 6.925 12.5 6.925C12.7347 6.925 12.925 6.73472 12.925 6.5V5H14V10H1Z"
          fill="#000000"
        />
      </svg>
      Size Chart
    </button>
    
    <!-- Modal -->
    <div class="size-chart-modal" data-sizechart-modal>
      <div class="size-chart-overlay" data-sizechart-overlay></div>
      <div class="size-chart-modal__content">
        <button class="size-chart-modal__close" data-sizechart-close>&times;</button>

        <div class="size_guide_image_container">
          <div class="size-guide-content">
            <!-- Left: Size guide image -->
            <div class="size-guide-wrapper" id="size-guide-wrapper">
              <img
                src="${sizeChartUrl}"
                alt="Size Guide"
                class="size-guide-image"
              />

              <div class="size-label size-a" data-label="A"></div>
              <div class="size-label size-b" data-label="B"></div>
              <div class="size-label size-c" data-label="C"></div>
              <div class="size-label size-d" data-label="D"></div>
            </div>

            <div id="product-specs" class="visible-product-specs">
              <h4>Measurements</h4>
              <p id="ms-a"></p>
              <p id="ms-b"></p>
              <p id="ms-c"></p>
              <p id="ms-d"></p>
            </div>

            <div id="product-specs-full" style="display:none;">
              ${specsContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// Custom function for upsell modal - updates product info when swatch is selected
// These are separate products, not variants, so we update the entire product display
function updateUpsellProduct(item, modalElement) {
  // Ensure we have the modal - if it's already the modal, use it; otherwise find it
  let modal = modalElement;
  if (!modal || !modal.hasAttribute || !modal.hasAttribute('data-upsell-modal')) {
    modal = document.querySelector("[data-upsell-modal]");
  }
  
  if (!modal) {
    console.error('Modal element not found', modalElement);
    return;
  }
  
  // Always get fresh elements from the modal
  const img = modal.querySelector("[data-upsell-image]");
  const price = modal.querySelector("[data-upsell-price]");
  const btnPrice = modal.querySelector("[data-upsell-price-btn]"); // Optional - may not exist (removed from button)
  const styleContainer = modal.querySelector("[data-upsell-style]");
  const styleContent = modal.querySelector("[data-upsell-style-content]");
  const title = modal.querySelector("[data-upsell-title]");
  const atc = modal.querySelector("[data-upsell-atc]");
  const descriptionContainer = modal.querySelector("[data-upsell-description]");
  const descriptionContent = modal.querySelector("[data-upsell-description-content]");

  // Check for required elements only (btnPrice is intentionally optional and was removed)
  if (!img || !price || !title || !atc) {
    // Silently return if required elements are missing (don't log - btnPrice is optional)
    return;
  }

  // Update title
  if (item.title) {
    title.textContent = item.title;
  }
  
  // Fix price display - strip HTML if present
  if (item.price) {
    const priceText = typeof item.price === 'string' ? item.price.replace(/<[^>]*>/g, '').trim() : item.price;
    price.textContent = priceText;
    // Only update btnPrice if it exists (it was removed from the button)
    if (btnPrice) {
      btnPrice.textContent = priceText;
    }
  }

  // Update style if available
  if (styleContainer && styleContent) {
    if (item.style && item.style.trim() !== '') {
      styleContent.textContent = item.style;
      styleContainer.style.display = 'block';
    } else {
      styleContainer.style.display = 'none';
    }
  }

  // Smooth fade transition for image change
  if (item.image) {
    // Ensure image URL is complete (add https: if needed)
    let imageUrl = item.image;
    if (imageUrl.startsWith('//')) {
      imageUrl = 'https:' + imageUrl;
    }
    
    img.classList.add("fade-out");
    
    setTimeout(() => {
      img.src = imageUrl;
      img.classList.remove("fade-out");
      img.classList.add("fade-in");
      
      // Remove fade-in class after transition completes
      setTimeout(() => {
        img.classList.remove("fade-in");
      }, 300);
    }, 150); // Half of transition time for fade-out
  }

  // Update image gallery thumbnails if item has images array
  const imageGallery = modal.querySelector("[data-upsell-image-thumbnails]");
  if (item.images && Array.isArray(item.images) && item.images.length > 1 && imageGallery) {
    renderImageGallery(item.images, imageGallery, img);
  } else if (imageGallery) {
    imageGallery.innerHTML = '';
    imageGallery.style.display = 'none';
  }

  // Update variant ID for add to cart (this is the product variant ID)
  if (item.variant) {
    atc.dataset.variantId = item.variant;
  }
  setUpsellAtcAvailability(atc, item.available !== false);

  const variantsContainer = modal.querySelector("[data-upsell-variants]");
  const priceEl = modal.querySelector("[data-upsell-price]");
  renderVariantOptions(item, variantsContainer, priceEl, atc);

  // Update description - prefer short_description metafield, fallback to description
  // Always show description (no accordion)
  if (descriptionContainer && descriptionContent) {
    // Check both short_description and description properties
    let descriptionText = '';
    
    // Handle short_description - could be string or empty string
    if (item.short_description) {
      descriptionText = typeof item.short_description === 'string' 
        ? item.short_description.trim() 
        : String(item.short_description).trim();
    }
    
    // Fallback to description if short_description is empty
    if (!descriptionText && item.description) {
      descriptionText = typeof item.description === 'string' 
        ? item.description.trim() 
        : String(item.description).trim();
    }
    
    if (descriptionText && descriptionText !== '') {
      descriptionContent.innerHTML = descriptionText;
      descriptionContainer.style.display = 'block';
    } else {
      // If no description for this variant, hide the container
      descriptionContainer.style.display = 'none';
    }
  }
}

// Close modal handler - handles both overlay and close button
document.addEventListener("click", function (e) {
  const closeTrigger = e.target.closest("[data-upsell-close]");
  if (!closeTrigger) return;
  
  // Don't close if clicking inside the modal box content (only close on overlay background or close button)
  const modalBox = e.target.closest(".upsell-modal-box");
  if (modalBox && !e.target.closest(".upsell-close")) {
    return;
  }
  
  const modal = document.querySelector("[data-upsell-modal]");
  if (!modal) return;
  
  modal.classList.add("is-hidden");
  
  // Re-enable body scroll when modal closes
  document.body.classList.remove("upsell-modal-open");
  
  // Reset placeholder when modal closes
  const elements = getModalCache();
  if (elements) {
    if (elements.ratingPlaceholder) {
      elements.ratingPlaceholder.style.display = 'block';
    }
    if (elements.rating) {
      elements.rating.style.display = 'none';
      elements.rating.innerHTML = '';
    }
  }
});

function groupFamilyProducts(products) {
  const groups = {
    "Limited Edition": [],
    "Iced": [],
    "XL Sizes": [],
    "Buffalo Horn": [],
    "Gem Cut": [],
    "Round": [],
    "Wood Temple": [],
    "Core": []
  };

  products.forEach(p => {
    const title = p.title.toUpperCase();
    let assigned = false;

    if (p.isLimitedEdition) {
      groups["Limited Edition"].push(p);
      assigned = true;
    }

    if (title.includes("ICED")) {
      groups["Iced"].push(p);
      assigned = true;
    }

    if (title.includes("XL")) {
      groups["XL Sizes"].push(p);
      assigned = true;
    }

    if (title.includes("RARE BLANC")) {
      groups["Buffalo Horn"].push(p);
      assigned = true;
    }

    if (title.includes("GEM CUT")) {
      groups["Gem Cut"].push(p);
      assigned = true;
    }

    if (title.includes("HYDRA OPUS")) {
      groups["Round"].push(p);
      assigned = true;
    }

    if (title.includes("HYDRA") || title.includes("SIGNATURE GEM CUT")) {
      groups["Wood Temple"].push(p);
      assigned = true;
    }

    if (!assigned) {
      groups["Core"].push(p);
    }
  });

  return groups;
}

// Use event delegation for swatch clicks - single listener on document
let swatchClickHandler = null;

function renderGroupedSwatches(groups, wrap, modal) {
  wrap.innerHTML = "";

  Object.entries(groups).forEach(([label, items]) => {
    if (items.length === 0) return;

    // Create heading for this group
    const heading = document.createElement("div");
    heading.className = "upsell-swatch-heading";
    heading.textContent = label + ":";
    wrap.appendChild(heading);

    // Create containers for mobile (color) and desktop (image) swatches for this group
    const colorSwatchRow = document.createElement("div");
    colorSwatchRow.className = "upsell-swatch-container mobile_show";

    const imageSwatchRow = document.createElement("div");
    imageSwatchRow.className = "upsell-image-swatch-container desktop_show";

    items.forEach(p => {
      const isSoldOut = p.available === false;
      const itemData = {
        title: p.title,
        variant: p.variant,
        image: p.image,
        images: p.images || [],
        price: p.price,
        style: p.style || '',
        short_description: p.short_description || '',
        variants: p.variants || [],
        option_names: p.option_names || [],
        available: p.available !== false
      };

      // Color swatch for mobile
      const colorSwatch = document.createElement("button");
      colorSwatch.className = "upsell-swatch" + (isSoldOut ? " upsell-swatch--sold-out" : "");
      colorSwatch.dataset.variantId = p.variant;
      colorSwatch.dataset.itemData = JSON.stringify(itemData);
      if (isSoldOut) colorSwatch.setAttribute("aria-label", (p.title || "This variant") + " – Sold out");

      // Set color styles - use CSS variables from the theme
      if (p.frame && p.lens) {
        colorSwatch.style.setProperty("--left", `var(--${p.frame})`);
        colorSwatch.style.setProperty("--right", `var(--${p.lens})`);
      } else if (p.frame) {
        colorSwatch.style.setProperty("--full", `var(--${p.frame})`);
      } else if (p.lens) {
        colorSwatch.style.setProperty("--full", `var(--${p.lens})`);
      } else {
        colorSwatch.classList.add("upsell-swatch--no-color");
      }

      if (isSoldOut) {
        const soldOutLabel = document.createElement("span");
        soldOutLabel.className = "upsell-swatch-sold-out-label";
        soldOutLabel.textContent = "Sold out";
        colorSwatch.appendChild(soldOutLabel);
      }
      colorSwatchRow.appendChild(colorSwatch);

      // Image swatch for desktop
      const imageSwatch = document.createElement("button");
      imageSwatch.className = "upsell-swatch-image" + (isSoldOut ? " upsell-swatch--sold-out" : "");
      imageSwatch.dataset.variantId = p.variant;
      imageSwatch.dataset.itemData = JSON.stringify(itemData);
      if (isSoldOut) imageSwatch.setAttribute("aria-label", (p.title || "This variant") + " – Sold out");

      // Create image element
      const img = document.createElement("img");
      img.src = p.image || '';
      img.loading = "lazy";
      img.className = "swatch-image";
      imageSwatch.appendChild(img);
      if (isSoldOut) {
        const soldOutLabel = document.createElement("span");
        soldOutLabel.className = "upsell-swatch-sold-out-label";
        soldOutLabel.textContent = "Sold out";
        imageSwatch.appendChild(soldOutLabel);
      }

      imageSwatchRow.appendChild(imageSwatch);
    });
    
    wrap.appendChild(colorSwatchRow);
    wrap.appendChild(imageSwatchRow);
  });
  
  // Store modal reference on wrap for event delegation
  wrap.dataset.upsellModal = 'true';
}

// Variant option click (same-product variants e.g. lens type)
document.addEventListener("click", function(e) {
  const btn = e.target.closest(".upsell-variant-option");
  if (!btn) return;
  const wrap = btn.closest("[data-upsell-variants]");
  if (!wrap) return;
  e.preventDefault();
  const modal = document.querySelector("[data-upsell-modal]");
  if (!modal) return;
  const atc = modal.querySelector("[data-upsell-atc]");
  const priceEl = modal.querySelector("[data-upsell-price]");
  if (atc) atc.dataset.variantId = btn.dataset.variantId;
  if (priceEl && btn.dataset.price) priceEl.textContent = btn.dataset.price;
  wrap.querySelectorAll(".upsell-variant-option").forEach(b => b.classList.remove("active"));
  btn.classList.add("active");
});

// Single event delegation listener for all swatch clicks (both color and image swatches)
if (!swatchClickHandler) {
  swatchClickHandler = function(e) {
    // Check if click is on swatch or its child elements (like img)
    const swatch = e.target.closest(".upsell-swatch, .upsell-swatch-image");
    if (!swatch) return;
    
    const wrap = swatch.closest("[data-upsell-swatches]");
    if (!wrap) return;
    
    // Find the actual modal container - use document.querySelector to ensure we get it
    const modal = document.querySelector("[data-upsell-modal]");
    if (!modal) {
      console.error('Modal not found');
      return;
    }
    
    // Prevent default only for buttons
    if (swatch.tagName === 'BUTTON') {
      e.preventDefault();
    }
    
    // Remove active from all swatches in the entire modal (both types)
    modal.querySelectorAll(".upsell-swatch, .upsell-swatch-image").forEach(s => s.classList.remove("active"));
    swatch.classList.add("active");
    
    // Parse item data
    const itemDataStr = swatch.dataset.itemData;
    if (!itemDataStr) {
      console.error('Swatch missing itemData', swatch);
      return;
    }
    
    try {
      const itemData = JSON.parse(itemDataStr);
      updateUpsellProduct(itemData, modal);
    } catch (error) {
      console.error('Error parsing swatch data:', error, itemDataStr);
    }
  };
  
  document.addEventListener("click", swatchClickHandler, true); // Use capture phase
}

// Add to cart handler
document.addEventListener("click", async function (e) {
  const btn = e.target.closest("[data-upsell-atc]");
  if (!btn) return;
  if (btn.disabled) return;

  const variantId = btn.dataset.variantId;
  const modal = document.querySelector("[data-upsell-modal]");
  
  // Check if priority checkbox is checked
  const priorityCheckbox = modal?.querySelector("[data-priority-checkbox]");
  const priorityVariantId = priorityCheckbox?.checked ? priorityCheckbox.dataset.priorityVariantId : null;

  // Add loading state
  btn.classList.add("loading");
  btn.setAttribute("aria-disabled", "true");

  try {
    // Build items array - add both products in a single request if priority is checked
    const items = [{ id: variantId, quantity: 1 }];
    if (priorityVariantId) {
      items.push({ id: priorityVariantId, quantity: 1 });
    }

    // Add all items to cart in a single request
    const addResponse = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: items }),
      credentials: "same-origin"
    });

    const addText = await addResponse.text();
    let addResult;
    try {
      addResult = addText ? JSON.parse(addText) : {};
    } catch (_) {
      addResult = {};
    }

    if (!addResponse.ok) {
      const errMsg = (addResult && addResult.description) || addResult.message || addResponse.statusText || "Unable to add to cart.";
      console.error("Error adding to cart:", addResult, addResponse.status);
      alert(errMsg);
      return;
    }

    if (addResult.errors && Object.keys(addResult.errors).length) {
      const errMsg = typeof addResult.errors === "string" ? addResult.errors : (addResult.description || "Error adding to cart. Please try again.");
      alert(errMsg);
      return;
    }

    // Get the updated sections
    const drawer = document.querySelector("cart-drawer");
    if (!drawer) {
      const modal = document.querySelector("[data-upsell-modal]");
      if (modal) {
        modal.classList.add("is-hidden");
        document.body.classList.remove("upsell-modal-open");
      }
      return;
    }

    drawer.classList.remove("is-empty");
    const sectionsToRender = drawer.getSectionsToRender().map(s => s.id).join(",");
    const res = await fetch(`/cart?sections=${sectionsToRender}`, { credentials: "same-origin" });
    const resText = await res.text();
    let html = {};
    try {
      html = resText ? JSON.parse(resText) : {};
    } catch (_) {}

    const payload = {
      id: variantId,
      sections: html
    };
    await drawer.renderContents(payload);

    const modal = document.querySelector("[data-upsell-modal]");
    if (modal) {
      modal.classList.add("is-hidden");
      document.body.classList.remove("upsell-modal-open");
    }
  } catch (error) {
    console.error("Error adding to cart:", error);
    alert("Unable to add to cart. Please try again.");
  } finally {
    // Remove loading state
    btn.classList.remove("loading");
    btn.removeAttribute("aria-disabled");
  }
});

